import { useEffect, useRef, useState } from 'react';
import { Target, Eye, Award, Users, Factory } from 'lucide-react';
import { companyInfo, timeline, honors } from '../data/company';

export default function About() {
  const [, setActiveSection] = useState('');
  const sectionRefs = {
    intro: useRef<HTMLDivElement>(null),
    timeline: useRef<HTMLDivElement>(null),
    honors: useRef<HTMLDivElement>(null)
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: 0.3 }
    );

    Object.values(sectionRefs).forEach((ref) => {
      if (ref.current) observer.observe(ref.current);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <main className="pt-20">
      <section className="py-16 md:py-24 bg-gradient-to-br from-deep-lake to-deep-lake-dark">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif-zh font-bold text-white mb-6">
            关于民间艺人
          </h1>
          <p className="text-lg md:text-xl text-white/80 max-w-3xl mx-auto">
            十余载匠心传承，专注每一支浮漂的极致品质
          </p>
        </div>
      </section>

      <section id="intro" ref={sectionRefs.intro} className="py-20 md:py-32 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block text-sm font-medium text-dawn-orange tracking-wider uppercase mb-4">
                Company Profile
              </span>
              <h2 className="text-3xl md:text-4xl font-serif-zh font-bold text-deep-lake mb-6">
                企业简介
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6 whitespace-pre-line">
                {companyInfo.description}
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-xl">
                  <Factory className="w-6 h-6 text-deep-lake mb-2" />
                  <div className="text-sm text-gray-500">成立时间</div>
                  <div className="font-semibold text-deep-lake">{companyInfo.founded}</div>
                </div>
                <div className="p-4 bg-gray-50 rounded-xl">
                  <Users className="w-6 h-6 text-deep-lake mb-2" />
                  <div className="text-sm text-gray-500">员工人数</div>
                  <div className="font-semibold text-deep-lake">{companyInfo.employees}</div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden">
                <img
                  src="/images/gc.png"
                  alt="民间艺人工厂"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 w-48 h-48 bg-warm-gold/20 rounded-2xl -z-10" />
            </div>
          </div>

          <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-8 bg-gradient-to-br from-deep-lake to-deep-lake-dark rounded-2xl text-white text-center">
              <div className="flex justify-center">
                <Target className="w-10 h-10 text-warm-gold mb-4" />
              </div>
              <h3 className="text-2xl font-serif-zh font-bold mb-4">使命</h3>
              <p className="text-white/80 leading-relaxed">{companyInfo.mission}</p>
            </div>
            <div className="p-8 bg-gradient-to-br from-warm-gold/20 to-dawn-orange/20 rounded-2xl text-center">
              <div className="flex justify-center">
                <Eye className="w-10 h-10 text-dawn-orange mb-4" />
              </div>
              <h3 className="text-2xl font-serif-zh font-bold text-deep-lake mb-4">愿景</h3>
              <p className="text-gray-600 leading-relaxed">{companyInfo.vision}</p>
            </div>
            <div className="p-8 bg-gradient-to-br from-deep-lake/10 to-deep-lake/20 rounded-2xl border-2 border-deep-lake/20 text-center">
              <div className="flex justify-center">
                <Award className="w-10 h-10 text-deep-lake mb-4" />
              </div>
              <h3 className="text-2xl font-serif-zh font-bold text-deep-lake mb-4">价值观</h3>
              <p className="text-gray-600 leading-relaxed">{companyInfo.values}</p>
            </div>
          </div>
        </div>
      </section>

      <section id="timeline" ref={sectionRefs.timeline} className="py-20 md:py-32 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-sm font-medium text-dawn-orange tracking-wider uppercase mb-4">
              Development History
            </span>
            <h2 className="text-3xl md:text-4xl font-serif-zh font-bold text-deep-lake mb-4">
              发展历程
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              从手工作坊到行业领军，每一步都铭刻着匠心的印记
            </p>
          </div>

          <div className="max-w-5xl mx-auto relative">
            {/* 中央时间线 */}
            <div className="absolute left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-deep-lake via-dawn-orange to-deep-lake -translate-x-1/2" />
            
            {timeline.map((item, index) => {
              const isLeft = index % 2 === 0;
              return (
                <div key={item.year} className="relative flex items-center mb-16 last:mb-0">
                  {/* 左侧内容 */}
                  <div className={`w-5/12 ${isLeft ? 'pr-8 text-right' : 'pr-8 opacity-0 pointer-events-none'}`}>
                    {isLeft && (
                      <>
                        <span className="inline-block px-4 py-1 bg-deep-lake text-white text-sm font-bold rounded-full mb-3">
                          {item.year}
                        </span>
                        <h3 className="text-xl font-serif-zh font-bold text-gray-900 mb-2">
                          {item.title}
                        </h3>
                        <p className="text-gray-600 leading-relaxed text-sm">{item.description}</p>
                      </>
                    )}
                  </div>
                  
                  {/* 中心节点 */}
                  <div className="w-2/12 flex justify-center">
                    <div className="relative">
                      <div className="w-6 h-6 rounded-full bg-dawn-orange border-4 border-white shadow-lg z-10 relative" />
                      {/* 发光效果 */}
                      <div className="absolute inset-0 w-6 h-6 rounded-full bg-dawn-orange/30 animate-ping" />
                    </div>
                  </div>
                  
                  {/* 右侧内容 */}
                  <div className={`w-5/12 ${!isLeft ? 'pl-8 text-left' : 'pl-8 opacity-0 pointer-events-none'}`}>
                    {!isLeft && (
                      <>
                        <span className="inline-block px-4 py-1 bg-deep-lake text-white text-sm font-bold rounded-full mb-3">
                          {item.year}
                        </span>
                        <h3 className="text-xl font-serif-zh font-bold text-gray-900 mb-2">
                          {item.title}
                        </h3>
                        <p className="text-gray-600 leading-relaxed text-sm">{item.description}</p>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section id="honors" ref={sectionRefs.honors} className="py-20 md:py-32 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-sm font-medium text-dawn-orange tracking-wider uppercase mb-4">
              Honors & Awards
            </span>
            <h2 className="text-3xl md:text-4xl font-serif-zh font-bold text-deep-lake mb-4">
              荣誉资质
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              每一份荣誉都是对品质的认可，对匠心的肯定
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
            {honors.map((honor, index) => (
              <div
                key={index}
                className="group rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300"
              >
                <div className="aspect-[4/3] overflow-hidden bg-gray-100">
                  <img
                    src={honor.image}
                    alt={honor.title}
                    className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>


    </main>
  );
}
